// ============================================================================
// Sport-icon resolver -- maps an event's snake_case `icon` key to a Material
// icon component. Shared by EventCard and the mobile calendar cards.
// ============================================================================

import {
  DirectionsBike,
  DirectionsRun,
  FitnessCenter,
  Pool,
  SelfImprovement,
  Favorite,
  IceSkating,
  SportsScore,
  MoreHoriz,
  Kayaking,
  Rowing,
} from '@mui/icons-material'
import type { SvgIconComponent } from '@mui/icons-material'
import { getSportIcon as getSportIconName } from '@/lib/workout-utils'
import { SPORT_DISPLAY, type GeneralActivityType } from '@/types/workouts'

const ICON_MAP: Record<string, SvgIconComponent> = {
  directions_bike: DirectionsBike,
  directions_run: DirectionsRun,
  fitness_center: FitnessCenter,
  pool: Pool,
  self_improvement: SelfImprovement,
  favorite: Favorite,
  ice_skating: IceSkating,
  sports_score: SportsScore,
  more_horiz: MoreHoriz,
  // Keys must match SPORT_TYPE_MAP in sportType.ts, or the icon silently
  // falls back to FitnessCenter while the colour resolves correctly.
  kayaking: Kayaking,
  rowing: Rowing,
}

export function getSportIcon(key: string | null | undefined): SvgIconComponent {
  return ICON_MAP[key ?? ''] ?? FitnessCenter
}

// Same icons, keyed by sport enum instead of an event's icon key. Derived from
// workout-utils' enum table rather than repeating it, so one place decides
// which sport wears which icon.
const BY_COMPONENT_NAME: Record<string, SvgIconComponent> = {
  DirectionsBike, DirectionsRun, FitnessCenter, Pool,
  SelfImprovement, Favorite, IceSkating, SportsScore, MoreHoriz, Kayaking,
}

export const SPORT_ICONS = Object.fromEntries(
  (Object.keys(SPORT_DISPLAY) as GeneralActivityType[])
    .map((sport) => [sport, BY_COMPONENT_NAME[getSportIconName(sport)] ?? FitnessCenter]),
) as Record<GeneralActivityType, SvgIconComponent>
